import {Box, Skeleton} from '@mantine/core';
import Head from 'next/head';
import {useState} from 'react';
import {TITLE_SUFFIX} from '../../../config/constants';
import {COMPANIES_CRUMB, HOME_CRUMB} from '../../../config/constants/crumb';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {COMPANIES, HOME} from '../../../config/constants/routes';
import {FOLLOW_COMPANY_RANKING_TERMS} from '../../../core/entities/company/constants';
import type {FollowRankingListTermType} from '../../../core/entities/company/schema';
import {
  type ResultGetWorkAttractiveConditions,
  useGetFollowCompanyRankingList,
  useInfiniteEngineerCompanyList,
} from '../../../core/repositories/companyRepository';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {useHandleCompanyForSearch} from '../usecase/useHandleCompanyForSearch';
import {useSyncAttractiveConditionWithQuery} from '../usecase/useSyncAttractiveConditionWithQuery';
import {CompanyListHeader} from './Container/CompanyListHeader';
import {CompanyCard} from './Container/Presenter/CompanyCard';
import {CompanyFilter} from './Container/Presenter/CompanyFilter';
import {FollowCompanyRankingFrame} from './Container/Presenter/FollowCompanyRankingFrame';

type Props = {
  attractiveConditionsData?: ResultGetWorkAttractiveConditions;
  attractiveConditionQuery?: string[];
};

export const CompanyListIndustryEngineerPage: React.FC<Props> = ({
  attractiveConditionsData,
  attractiveConditionQuery = [],
}: Props) => {
  const [filterAttractiveConditions, setFilterAttractiveConditions] =
    useSyncAttractiveConditionWithQuery(attractiveConditionQuery);
  const {
    searchWord,
    handleSearchInputChange,
    handleFilterEmployeeCountChange,
    filterMinEmployeeCount,
    filterMaxEmployeeCount,
    attractiveConditionsList,
  } = useHandleCompanyForSearch(
    undefined,
    attractiveConditionsData,
    filterAttractiveConditions,
  );

  const {data, isLoading: isLoadingCompanies} = useInfiniteEngineerCompanyList({
    queryParams: {
      limit: 100,
      page: 1,
      minEmployeeCount: filterMinEmployeeCount,
      maxEmployeeCount: filterMaxEmployeeCount,
      workAttractiveConditionIDs: filterAttractiveConditions.map((item) =>
        Number(item),
      ),
    },
  });
  const industryCompanies = data?.pages.flatMap((page) => page.companies) ?? [];
  const companiesForSearch = industryCompanies.map((c) => {
    return {
      id: c.id,
      name: c.name,
    };
  });

  const [followRankingListTerm, setFollowRankingListTerm] =
    useState<FollowRankingListTermType>(FOLLOW_COMPANY_RANKING_TERMS.weekly);
  const {data: followRankingList, isLoading: isLoadingFollowRankingList} =
    useGetFollowCompanyRankingList({
      limit: 10,
      isEngineerCompany: true,
      term:
        followRankingListTerm === FOLLOW_COMPANY_RANKING_TERMS.all
          ? undefined
          : followRankingListTerm,
    });

  const isDisPlayFollowRanking =
    (isLoadingFollowRankingList ||
      (followRankingList?.ok && followRankingList.value.ranking.length > 0)) ??
    false;

  const industryName = 'エンジニア';

  const pageTitle = `${industryName}の企業一覧`;
  const url = `${GSSKT_HOST}/company/industry/engineer`;
  const description =
    'インターン募集、セミナー、説明会、選考会など、厳選した外資・日系トップ企業の新卒採用に関する最新情報を随時掲載';

  const crumbs = [
    {label: HOME_CRUMB, href: HOME},
    {label: COMPANIES_CRUMB, href: COMPANIES},
    {
      label: pageTitle,
      href: `${COMPANIES}/industry/engineer`,
    },
  ];

  const {isPC} = useMediaQuery();
  const width =
    typeof window === 'undefined' || isPC
      ? 282
      : (window?.screen.width ?? 282) - 32;

  const companiesLength = industryCompanies.length;

  return (
    <>
      <Head>
        <title key="title">{`${pageTitle}${TITLE_SUFFIX}`}</title>
        <meta key="og:title" property="og:title" content={pageTitle} />
        <meta key="og:url" property="og:url" content={url} />
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content="外資・日系トップ企業,企業一覧,外資,就活,インターンシップ,新卒採用,会社説明会,就活コラム,ES・体験記,注目企業"
        />
        <link rel="canonical" href={url} key="canonical" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,maximum-scale=1.0"
        />
      </Head>
      <Box className="hidden pc:block">
        <GoogleAdBanner
          googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
        />
      </Box>
      <Box className="w-full mx-auto flex pc:w-[1200px] justify-between">
        <Box className="w-full pc:w-[1200px]">
          <CompanyListHeader pageTitle={pageTitle} crumbs={crumbs} />
          <Box className="bg-white mx-16 rounded-16 shadow-card pc:bg-transparent pc:mx-0 pc:rounded-none pc:shadow-transparent">
            <CompanyFilter
              companies={companiesForSearch}
              searchWord={searchWord}
              handleSearchInputChange={handleSearchInputChange}
              handleFilterEmployeeCountChange={handleFilterEmployeeCountChange}
              attractiveConditionsList={attractiveConditionsList}
              handleFilterAttractiveConditionChange={
                setFilterAttractiveConditions
              }
              filterAttractiveConditions={filterAttractiveConditions}
            />
          </Box>
        </Box>
      </Box>
      <div className="w-full pc:w-[1200px] pc:mx-auto">
        {isDisPlayFollowRanking && (
          <FollowCompanyRankingFrame
            ranking={followRankingList}
            isLoadingRanking={isLoadingFollowRankingList}
            rankingTerm={followRankingListTerm}
            setRankingTerm={setFollowRankingListTerm}
            industryIds={[]}
            industryCategoryId={0}
            isEngineerCompany={true}
          />
        )}
      </div>
      <div className="pc:w-[1200px] grid grid-cols-1 pc:grid-cols-4 mt-8 gap-16 pc:gap-24">
        {industryCompanies.map((company, index) => (
          <CompanyCard
            key={`card-${index}`}
            company={company}
            width={width}
            bgColorStyle={'bg-transparent'}
          />
        ))}
        {isLoadingCompanies &&
          Array.from({length: 20}).map((_, index) => (
            <div key={index}>
              <Skeleton height={150} width={282} />
              <Skeleton height={30} width={282} />
              <Skeleton height={30} width={282} />
            </div>
          ))}
      </div>
      {companiesLength === 0 && !isLoadingCompanies && (
        <div className="w-full py-16 px-16 pc:px-0">
          <div className="w-full flex justify-center items-center">
            <p className="text-black-2">企業が見つかりませんでした。</p>
          </div>
        </div>
      )}
    </>
  );
};
